import {
  DeliveryContent,
  DeliveryImplantInspection,
  HistoricalSwitch,
  LabeledFilter,
  SelectedRate,
  ShipperDetails,
  Signature,
} from './Deliveries';
import { ImplantsByDelivery } from './Implants';
import { PageableRequest, PageableResponse } from './Pageable';
import { KitInstanceId, SetsByDelivery } from './Sets';
import { SortOrder } from './Sorting';

export const ORDER_FOR_CHECK_IN_STATUSES: OrderStatus[] = ['OUTBOUND', 'RECEIVE', 'PARTIALLY_CHECKED_IN'];

export type OrderStatus =
  | 'Default'
  | 'REQUESTED'
  | 'BOOKED'
  | 'ORDERED'
  | 'PARTIALLY_DISPATCHED'
  | 'OUTBOUND' // DISPATCHED
  | 'INBOUND'
  | 'PARTIALLY_DELIVERED'
  | 'DELIVERED'
  | 'PARTIALLY_RECEIVED'
  | 'RECEIVE'
  | 'INSPECTION'
  | 'PARTIALLY_CHECKED_IN'
  | 'CHECK_IN'
  | 'PARTIALLY_CHECKED_OUT'
  | 'CHECK_OUT'
  | 'PARTIALLY_RETURNED'
  | 'RETURNED'
  | 'DELETED'
  | 'CANCELED';

export enum OrdersSortField {
  // shared
  Surgeon = 'surgeon',
  SurgeryDate = 'surgeryDate',

  // supplier user
  Hospital = 'hospital',
  OrderNumber = 'orderNumber',
  DeliveryNumber = 'deliveryNumber',
  DueDate = 'dueDate',
  Express = 'express',

  // hospital user
  SurgeryId = 'surgeryReference',
  DeviceManufacturerRep = 'dmRep',
  Supplier = 'supplier',
  SurgeryDescription = 'surgeryDescription',
  SurgeryComments = 'surgeryComments',
}

export type OrdersFilters = {
  sort_order?: SortOrder;
  sort_by?: OrdersSortField;
  status?: OrderStatus;
  // if the logged user is a hospital user, this is the supplier tenant id,
  // else if the logged user is a supplier user, this is the hospital tenant id
  tenant_id?: string | LabeledFilter;
  surgeon_name?: string;
  surgery_date?: string;
  search?: string;
  active_hospital?: string;
  patient_initials?: string;
  historical?: HistoricalSwitch;
};

export type OrdersRequestParams = PageableRequest & OrdersFilters;

export type OrderRecordId = string;

export type AllOrdersFilters = {
  search?: string;
  status?: OrderStatus;
  surgery_date?: string;
  supplier_tenant_id?: string;
  active_hospital?: string;
  hospital_tenant_id?: string;
};

export type AllOrdersHospital = {
  tenantId: string;
  name: string;
  description: string | null;
};

export type AllOrdersHospitalsResponse = { hospitals: AllOrdersHospital[] };

export type AllOrdersRequestParams = PageableRequest & AllOrdersFilters;

export type AllOrdersOrder = {
  orderRecordId: OrderRecordId;
  orderNumber: string;
  status: OrderStatus;
  supplierTenantId: string;
  supplierName: string;
  hospitalTenantId: string;
  hospitalName: string;
  hospitalDescription: string | null;
  surgeonName: string | null;
  surgeryDate: string;
  deliveryNumbers: string[];
  shipmentIds: string[];
};

export type AllOrdersResponse = PageableResponse<AllOrdersOrder>;

export type DeliveryInOrder = {
  deliveryId: number;
  deliveryNumber: string;
  // only populated when fetching a single order, left undefined in list responses
  deliveryNumberAutoGenerated?: boolean;
  deliveryStage: OrderStatus; // delivery.last_completed_stage is an order_stage
  deliveryContent: DeliveryContent;
  express: boolean;
  turnaroundOrigin: boolean;
  turnaroundDestination: boolean;
  shipmentId?: string;
  shipper?: string;
};

export const makeDeliveryInOrder = (data: Partial<DeliveryInOrder>): DeliveryInOrder => ({
  deliveryId: 0,
  deliveryNumber: '',
  deliveryNumberAutoGenerated: false,
  deliveryStage: 'Default',
  deliveryContent: DeliveryContent.Default,
  express: false,
  turnaroundOrigin: false,
  turnaroundDestination: false,
  ...data,
});

export type OrderSummary = {
  orderRecordId: OrderRecordId;
  orderNumber: string;
  patientInitials?: string;
  hospitalName: string;
  hospitalDescription?: string;
  manufacturerName?: string;
  tenantId: string; // supplier id if hospital user else hospital id
  supplierCreated?: boolean;
  deviceRepId?: string;
  surgeryDate: string;
  surgeryReference?: string;
  surgeryDescription?: string;
  surgeryComments?: string;
  surgeonName: string;
  traysCount?: number;
  implantsCount?: number;
  estimatedCheckOutDate: string;
  status: OrderStatus;
  express: boolean | null;
  deviceMissing: boolean;
  deliveries: DeliveryInOrder[];
  createdBy?: string;
};

export type OrdersResponse = PageableResponse<OrderSummary>;

export type OrderResponse = {
  orderNumber: string;
  surgery: {
    surgeryId: string;
    surgeryDate: string;
    patientInitials?: string;
    surgeryReference?: string;
    surgeryComments?: string;
    surgeryDescription?: string;
    surgeryColorCode?: string;
    surgeonName: string;
    surgeonId: string;
    clinicalArea: string;
  };
  hospitalDetails: {
    name: string;
    tenantId: string;
    locationId: string;
  };
  supplierDetails: {
    name: string;
    tenantId: string;
    deviceRepId?: string;
  };
  supplierCreated?: boolean;
  status: OrderStatus;
  dueDate: string;
  content: DeliveryContent;
  numTraysReceived: number;
  numTraysSentSterilising: number;
  shipperDetails?: ShipperDetails;
  sets: SetsByDelivery;
  implants: ImplantsByDelivery;
  deliveries?: DeliveryInOrder[];
  createdBy?: string;
  implantInspection?: Record<string, DeliveryImplantInspection>;
  selectedRate?: SelectedRate;
  signature?: Signature;
};

export const makeOrder = (data: Partial<OrderResponse>): OrderResponse => ({
  orderNumber: '',
  surgery: {
    surgeryId: '',
    surgeryDate: '',
    patientInitials: '',
    surgeryReference: '',
    surgeryComments: '',
    surgeryDescription: '',
    surgeryColorCode: '',
    surgeonName: '',
    surgeonId: '',
    clinicalArea: '',
  },
  hospitalDetails: {
    name: '',
    tenantId: '',
    locationId: '',
  },
  supplierDetails: {
    name: '',
    tenantId: '',
    deviceRepId: '',
  },
  supplierCreated: false,
  status: 'Default',
  dueDate: '',
  content: DeliveryContent.Default,
  numTraysReceived: 0,
  numTraysSentSterilising: 0,
  shipperDetails: {
    numRoadCases: 0,
    numTubs: 0,
    numBoxes: 0,
    numPelicanCases: 0,
    numDoubleRoadCases: 0,
  },
  sets: [],
  implants: {},
  deliveries: [],
  createdBy: '',
  implantInspection: {},
  ...data,
});

export type OrderCsv = {
  order_number: string;
  delivery_number: string;
  hospital: string;
  surgeon: string;
  surgery_date: string;
  set_code: string;
  unique_serial_code: string;
  delivery_content: string | null;
  clinical_area: string | null;
  surgery_reference: string | null;
  order_status: string | null;
  set_plant_location: string | null;
};

export type OrderBody = {
  orderNumber: string;
  deliveryNumber: string;
  hospital: string;
  surgeon: string;
  surgeryDate: string;
  setCode: string;
  uniqueSerialCode: string;
  deliveryContent: string | null;
  clinicalArea: string | null;
  surgeryReference: string | null;
  orderStatus: string | null;
  setPlantLocation: string | null;
};

type ValidateOrderUpdateOrderDetails = {
  orderNumber: string;
  orderStatus: string;
  hospitalName: string;
  surgeonName: string;
  surgeryDate: string;
  surgeryReference: string | null;
  clinicalArea: string | null;
};

type OrderNumber = string;
type DeliveryNumber = string;
type SetName = string;

export type ValidateOrderResult = OrderBody & {
  status: 'APPROVED' | 'REVIEW' | 'REJECTED';
  message:
    | 'SUCCESS'
    | 'HOSPITAL_NOT_FOUND'
    | 'MULTIPLE_HOSPITALS_FOUND'
    | 'SURGEON_NOT_FOUND'
    | 'MULTIPLE_SURGEONS_FOUND'
    | 'UPDATE_ORDER_DETAILS'
    | 'INVALID_DATE_FORMAT'
    | 'INVALID_SURGERY_REFERENCE'
    | null;
  deliveriesWithSets: Record<
    DeliveryNumber,
    {
      deliveryNumber: DeliveryNumber;
      deliveryContent: string | null;
      sets: Record<
        SetName,
        {
          setName: SetName;
          serial: string;
          message: 'USS_ATTACHED_TO_DIFFERENT_SET' | null;
        }
      >;
      message: 'DELIVERY_NUMBER_OWNED' | 'DELIVERY_NUMBER_DUPLICATED' | null;
    }
  >;
  orderUpdateDiff: {
    existingOrderDetails: ValidateOrderUpdateOrderDetails;
    newOrderDetails: ValidateOrderUpdateOrderDetails;
  } | null;
};

type HospitalName = string;

export type ValidateOrdersResponse = {
  orders: Record<OrderNumber, ValidateOrderResult>;
  possibleMatches: {
    hospitals: Array<{
      location_id: string;
      name: HospitalName;
      description: string;
      region: string;
    }>;
    surgeons: Record<
      HospitalName,
      Array<{
        id: string;
        name: string;
        status: boolean;
      }>
    >;
    orderStatuses: null; // TODO: SS what is this?
  };
};

// This is our derived state based on the orders csv that was uploaded and the validate_orders response that we will use to render the UI
export type PendingOrder = OrderBody & {
  status: ValidateOrderResult['status'];
  message:
    | ValidateOrderResult['message']
    | ValidateOrderResult['deliveriesWithSets'][DeliveryNumber]['message']
    | ValidateOrderResult['deliveriesWithSets'][DeliveryNumber]['sets'][SetName]['message'];
  possibleMatches:
    | ValidateOrdersResponse['possibleMatches']['hospitals']
    | ValidateOrdersResponse['possibleMatches']['surgeons'][HospitalName];
  orderUpdateDiff: ValidateOrderResult['orderUpdateDiff'];
};

export type PendingOrders = {
  approve: PendingOrder[];
  review: PendingOrder[];
  merge: PendingOrder[];
  reject: PendingOrder[];
};

// This is what we will send to the BE
export type CreateOrder = OrderBody & {
  importMode: 'create' | 'merge' | 'ignore';
};

type CreateOrderResult = {
  orderNumber: string;
  deliveryNumber: string;
  result: 'success' | 'failure';
  message?: string;
};

export type CreateOrdersResponse = {
  success: CreateOrderResult[];
  failure: CreateOrderResult[];
};

export type AddTurnaroundSetsToOrderRequest = {
  kit_instance_ids: KitInstanceId[];
};

export type ReturnOrderParams = {
  orderRecordId: string;
  returnDetails?: ShipperDetails;
  returnedByRepId?: string;
};
